/**
 * Journey lifecycle actions (v2.5 SS-02 / PR B2, rounds 1–2).
 *
 * The workspace affordances for the deliberate transitions — complete /
 * abandon / reopen — plus the G0 RECOVERY operation: a server-validated
 * REVERT that references the transition's audit event (reverts_event_id)
 * and restores its exact prior state (an abandoned Draft reverts to Draft).
 *
 * Role UX (approved G0 matrix; the SERVER remains the security control):
 *  - viewer: NO mutation controls at all.
 *  - analyst: Mark complete only when readyForReview; Reopen/Revert only for
 *    their own transition within 24h; no Abandon.
 *  - admin: everything.
 *
 * EVERY transition confirms first through a dialog implementing the G0
 * contract: accessible name with INITIAL FOCUS ON THE HEADING, focus trap,
 * Escape, focus restoration (isConnected-guarded, with a stable fallback
 * when the trigger disappeared), the journey name, a consequence list, the
 * EXACT audit event disclosure, a verb-specific confirm button, and a
 * reason field whose whitespace rejection sets aria-invalid, links the
 * error, and MOVES FOCUS back to the field.
 *
 * The recovery surface derives from the DURABLE summary.lastTransition (the
 * audit-trail anchor) — it survives refresh and disappears exactly when
 * dismissed or superseded by a newer transition.
 */

import React, { useRef, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { useAuth } from '../../contexts/AuthContext';
import type { JourneyStatusSummary, JourneyLifecycle } from '../../types/journeyStatus';

interface LifecycleRequest {
  action: 'complete' | 'abandon' | 'reopen' | 'revert';
  reverts_event_id?: string;
}

interface DialogSpec {
  request: LifecycleRequest;
  title: string;
  consequences: string[];
  confirmLabel: string;
  needsReason: boolean;
}

const REOPEN_SELF_SERVICE_MS = 24 * 60 * 60 * 1000;

const LIFECYCLE_WORD: Record<JourneyLifecycle, string> = {
  draft: 'Draft',
  in_progress: 'In progress',
  completed: 'Completed',
  abandoned: 'Abandoned',
};

/** The exact audit disclosure (G0): event name + relevant payload. */
function auditLine(request: LifecycleRequest): string {
  const payload =
    request.action === 'revert'
      ? `transition: revert, reverts_event_id: ${request.reverts_event_id}`
      : `transition: ${request.action}`;
  return `Recorded in the audit log as "journey_status_changed" (${payload}) with your name, role, and timestamp.`;
}

interface JourneyLifecycleActionsProps {
  groupId: string;
  journeyName: string;
  summary: JourneyStatusSummary;
  onTransitioned: () => void;
}

export const JourneyLifecycleActions: React.FC<JourneyLifecycleActionsProps> = ({
  groupId,
  journeyName,
  summary,
  onTransitioned,
}) => {
  const { authFetch } = useApiClient();
  const { user } = useAuth();
  const [dialog, setDialog] = useState<DialogSpec | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Recovery-bar dismissal is local UI state; the BAR ITSELF is durable
   *  (summary.lastTransition), keyed by event so a new transition re-shows. */
  const [dismissedRecoveryEventId, setDismissedRecoveryEventId] = useState<string | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);

  const role = user?.role ?? 'viewer';

  // ------------------------------------------------------------------------
  // Role matrix (client UX; server enforces)
  // ------------------------------------------------------------------------
  const inProgressOrDraft = summary.lifecycle === 'draft' || summary.lifecycle === 'in_progress';
  const canCompleteReady = inProgressOrDraft && summary.readyForReview && role !== 'viewer';
  const canCompleteOverride = inProgressOrDraft && !summary.readyForReview && role === 'admin';
  const canAbandon = inProgressOrDraft && role === 'admin';
  const analystOwnRecentCompletion =
    summary.lifecycle === 'completed' &&
    role === 'analyst' &&
    summary.completedBy != null &&
    summary.completedBy === user?.id &&
    summary.completedAt != null &&
    Date.now() - new Date(summary.completedAt).getTime() <= REOPEN_SELF_SERVICE_MS;
  const canReopen =
    (summary.lifecycle === 'completed' || summary.lifecycle === 'abandoned')
      ? role === 'admin' || analystOwnRecentCompletion
      : false;

  // Revert permission mirrors the server: admin any; analyst their OWN
  // transition within 24h (actor + timestamp from the durable anchor).
  const recovery = summary.lastTransition;
  const canRevert =
    !!recovery &&
    (role === 'admin' ||
      (role === 'analyst' &&
        recovery.actorUserId != null &&
        recovery.actorUserId === user?.id &&
        Date.now() - new Date(recovery.at).getTime() <= REOPEN_SELF_SERVICE_MS));
  const showRecoveryBar =
    !!recovery && canRevert && recovery.eventId !== dismissedRecoveryEventId;

  // Viewers get no mutation surface at all.
  if (role === 'viewer') return null;

  // ------------------------------------------------------------------------
  // Transitions
  // ------------------------------------------------------------------------
  const restoreFocus = () => {
    // isConnected guard: a successful transition can re-render this surface
    // and unmount the trigger — fall back to the stable actions container.
    const trigger = triggerRef.current;
    if (trigger && trigger.isConnected) {
      trigger.focus();
    } else {
      containerRef.current?.focus();
    }
  };

  const send = async (request: LifecycleRequest, withReason?: string) => {
    setBusy(true);
    setError(null);
    try {
      const response = await authFetch(`/api/journey/${groupId}/lifecycle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...request, ...(withReason ? { reason: withReason } : {}) }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error ?? `Transition failed (${response.status})`);
      }
      setDialog(null);
      setReason('');
      setReasonError(null);
      onTransitioned();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Transition failed');
    } finally {
      setBusy(false);
      restoreFocus();
    }
  };

  const openDialog = (spec: DialogSpec, e: React.MouseEvent<HTMLButtonElement>) => {
    triggerRef.current = e.currentTarget;
    setReason('');
    setReasonError(null);
    setError(null);
    setDialog(spec);
  };

  const confirmDialog = () => {
    if (dialog?.needsReason && reason.trim().length === 0) {
      setReasonError('A written reason is required — whitespace is not a reason.');
      // G0: whitespace rejection MOVES FOCUS back to the reason field.
      reasonRef.current?.focus();
      return;
    }
    if (dialog) void send(dialog.request, dialog.needsReason ? reason : undefined);
  };

  return (
    <div className="jsp-actions" ref={containerRef} tabIndex={-1}>
      {canCompleteReady && (
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={busy}
          onClick={(e) =>
            openDialog(
              {
                request: { action: 'complete' },
                title: `Complete "${journeyName}"?`,
                consequences: [
                  'The journey is marked Completed with your name and role as the completing actor.',
                  'Analysis stops being the expected next step; results stay available.',
                  'It can be reverted or reopened later (admins any time; you within 24 hours).',
                  auditLine({ action: 'complete' }),
                ],
                confirmLabel: 'Complete journey',
                needsReason: false,
              },
              e
            )
          }
        >
          Mark complete
        </button>
      )}
      {canCompleteOverride && (
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={busy}
          onClick={(e) =>
            openDialog(
              {
                request: { action: 'complete' },
                title: `Complete "${journeyName}" with exceptions?`,
                consequences: [
                  'This journey is NOT ready for review — required components are incomplete.',
                  'It will render as "Completed with exceptions" with your reason shown, never as an unqualified completion.',
                  'It can be reverted or reopened later.',
                  auditLine({ action: 'complete' }),
                ],
                confirmLabel: 'Complete with exceptions',
                needsReason: true,
              },
              e
            )
          }
        >
          Complete with exceptions…
        </button>
      )}
      {canAbandon && (
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={busy}
          onClick={(e) =>
            openDialog(
              {
                request: { action: 'abandon' },
                title: `Abandon "${journeyName}"?`,
                consequences: [
                  'Work on this journey stops; it leaves active rollups.',
                  'All results and history are KEPT — nothing is deleted.',
                  'It can be reverted (restoring its prior state) or reopened by an administrator.',
                  auditLine({ action: 'abandon' }),
                ],
                confirmLabel: 'Abandon journey',
                needsReason: true,
              },
              e
            )
          }
        >
          Abandon…
        </button>
      )}
      {canReopen && (
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={busy}
          onClick={(e) =>
            openDialog(
              {
                request: { action: 'reopen' },
                title: `Reopen "${journeyName}"?`,
                consequences: [
                  'The journey returns to In progress.',
                  'The completion record clears from the journey (it is preserved in the audit trail).',
                  auditLine({ action: 'reopen' }),
                ],
                confirmLabel: 'Reopen journey',
                needsReason: false,
              },
              e
            )
          }
        >
          Reopen
        </button>
      )}

      {/* DURABLE recovery bar (G0): derived from summary.lastTransition — the
          audit-trail anchor — so it survives refresh and disappears exactly
          when dismissed or superseded. The action is a server-validated
          REVERT restoring the transition's EXACT prior state. */}
      {showRecoveryBar && recovery && (
        <div className="jsp-recovery-bar" role="status">
          <span>
            Journey {recovery.action === 'complete' ? 'completed' : 'abandoned'}
            {` on ${new Date(recovery.at).toLocaleDateString()}`}. Not what you meant?
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm"
            disabled={busy}
            onClick={(e) =>
              openDialog(
                {
                  request: { action: 'revert', reverts_event_id: recovery.eventId },
                  title: `Revert "${journeyName}" to ${LIFECYCLE_WORD[recovery.from]}?`,
                  consequences: [
                    `Restores the journey to exactly where it was before the ${recovery.action} — ${LIFECYCLE_WORD[recovery.from]}.`,
                    'The revert is itself an audited transition referencing the original event — nothing is silently rolled back.',
                    auditLine({ action: 'revert', reverts_event_id: recovery.eventId }),
                  ],
                  confirmLabel: `Revert to ${LIFECYCLE_WORD[recovery.from]}`,
                  needsReason: false,
                },
                e
              )
            }
          >
            Revert to {LIFECYCLE_WORD[recovery.from]} (audited)
          </button>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={() => setDismissedRecoveryEventId(recovery.eventId)}
          >
            Dismiss
          </button>
        </div>
      )}

      {error && (
        <div className="jsp-action-error" role="alert">
          {error}
        </div>
      )}

      {dialog && (
        <ConfirmDialog
          spec={dialog}
          reason={reason}
          reasonError={reasonError}
          busy={busy}
          reasonRef={reasonRef}
          onReasonChange={(v) => {
            setReason(v);
            if (reasonError && v.trim().length > 0) setReasonError(null);
          }}
          onCancel={() => {
            setDialog(null);
            setReason('');
            setReasonError(null);
            restoreFocus();
          }}
          onConfirm={confirmDialog}
        />
      )}
    </div>
  );
};

// ============================================================================
// Confirmation dialog — G0 contract: accessible name with INITIAL FOCUS ON
// THE HEADING, focus trap, Escape, linked reason validation (focus moves to
// the field on error — handled by the caller's confirm), verb-specific
// confirm. Focus restoration is the caller's job via its trigger ref.
// ============================================================================

interface ConfirmDialogProps {
  spec: DialogSpec;
  reason: string;
  reasonError: string | null;
  busy: boolean;
  reasonRef: React.RefObject<HTMLTextAreaElement | null>;
  onReasonChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  spec,
  reason,
  reasonError,
  busy,
  reasonRef,
  onReasonChange,
  onCancel,
  onConfirm,
}) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // Initial focus: the NAMED HEADING (G0) — screen readers announce the
  // dialog's purpose before any control.
  React.useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key === 'Tab') {
      // Focus trap: cycle within the dialog (heading participates via -1 but
      // is skipped by Tab; wrap across the tabbable controls).
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), textarea, [href], input, select'
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === headingRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const titleId = 'jsp-dialog-title';
  const errorId = 'jsp-dialog-reason-error';

  return (
    <div className="jsp-reason-backdrop" onKeyDown={onKeyDown}>
      <div
        className="jsp-reason-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <h3 id={titleId} tabIndex={-1} ref={headingRef}>
          {spec.title}
        </h3>
        <ul className="jsp-dialog-consequences">
          {spec.consequences.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        {spec.needsReason && (
          <>
            <textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="Reason (required)"
              aria-label="Reason for this transition"
              aria-invalid={reasonError ? true : undefined}
              aria-describedby={reasonError ? errorId : undefined}
              ref={reasonRef}
            />
            {reasonError && (
              <div id={errorId} className="jsp-action-error" role="alert">
                {reasonError}
              </div>
            )}
          </>
        )}
        <div className="jsp-reason-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onConfirm} disabled={busy}>
            {busy ? 'Applying…' : spec.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JourneyLifecycleActions;
